import Emitter from 'utils/emitter'
import {
    GLOBAL_CONSTANTS,
    isTablet,
} from 'utils/constants'
import {throttle} from 'throttle-debounce'
import {
    disableBodyScroll,
    enableBodyScroll,
    clearAllBodyScrollLocks,
} from 'body-scroll-lock'
import * as Sentry from '@sentry/browser'
import {setCustomColors, resetCustomColors} from '../utils/customColors'

/**
 * Adds Active state to Nav after page transitions.
 *
 * Makes the nav show / hide based on
 * scroll position / direction.
 */
const SCROLL_UP_BUFFER = 40

const CLASSES = {
    COMPONENT: '.js-navigation',
    NAV_LINK: '.js-nav-menu-link',
    NAV_MENU: '.js-nav-menu',
    SEARCH_ICON: '.js-search-icon',
    SEARCH_WRAPPER: '.js-search-wrapper',
    SEARCH_SCRIM: '.js-search-scrim',
    SEARCH_INPUT: '.js-search-input',
    SEARCH_FORM: '.js-search-form',
    REAL_TIME_SEARCH: '.real-time-search__wrapper',
}



export default class Nav {
    /**
     * @desc Set up nav with elements and bind events.
     * @param {HTMLElement} el - Element that contains possible sub-navigations
     *
     */
    constructor() {
        this.el = document.querySelector(CLASSES.COMPONENT)
        if (!this.el) {
            return
        }


        this.searchIcon = this.el.querySelector(CLASSES.SEARCH_ICON)
        this.searchWrapper = document.querySelector(CLASSES.SEARCH_WRAPPER)
        this.searchScrim = document.querySelector(CLASSES.SEARCH_SCRIM)
        this.searchForm = document.querySelector(CLASSES.SEARCH_FORM)

        this.throttleScroll = null
        this.scrollPosition = 0
        this.navMenuToggle = this.el.querySelector(CLASSES.NAV_LINK)
        this.navMenu = this.el.querySelector(CLASSES.NAV_MENU)
        this.tabItems = [
            this.navMenuToggle,
            ...Array.from(this.navMenu.getElementsByTagName('A')),
        ]

        this.toggleSearchBar = this.toggleSearchBar.bind(this)
        this.openSearchBar = this.openSearchBar.bind(this)
        this.closeSearchBar = this.closeSearchBar.bind(this)

        this.toggleNavMenu = this.toggleNavMenu.bind(this)
        this.handleResize = this.handleResize.bind(this)
        this.handleMenuKeyDown = this.handleMenuKeyDown.bind(this)

        this.closeNavMenu = this.closeNavMenu.bind(this)
        this.initialize()
        this.handleScroll()
    }

    /**
     * @desc initialize the class functions after global variables are defined
     */
    initialize() {
        this.registerEvents()
    }

    registerEvents() {
        // The back/forward cache hands the page back with the DOM exactly as it
        // was left, so a menu or search panel open at navigation comes back open,
        // body scroll lock and all. `popstate` is not the signal for that — a
        // restore is a fresh document event, and `window.onpopstate` is a single
        // slot CompanyFilters and TeamFilters claim for themselves.
        window.addEventListener('pageshow', (event) => {
            if (event.persisted) {
                this.closeNavMenu()
                this.closeSearchBar()
            }
        })

        this.throttleScroll = throttle(
            GLOBAL_CONSTANTS.TIMING.NAV_SCROLL_THROTTLE,
            this.handleScroll.bind(this),
        )
        Emitter.on(GLOBAL_CONSTANTS.EVENTS.SCROLL, this.throttleScroll)
        Emitter.on(GLOBAL_CONSTANTS.EVENTS.RESIZE, this.handleResize)

        this.searchIcon.addEventListener('click', this.toggleSearchBar)
        // The form has nowhere to submit to: results only ever come from
        // RealTimeSearch, which binds its own handler once its chunk lands.
        // Bound here so Enter before then cannot navigate away.
        this.searchForm.addEventListener('submit', (event) => event.preventDefault())
        this.searchScrim.addEventListener('click', this.closeSearchBar)
        this.navMenuToggle.addEventListener('click', this.toggleNavMenu)

        // Close search when clicking the wrapper's background (not on interactive elements)
        this.searchWrapper.addEventListener('click', (e) => {
            if (!e.target.closest('a, button, input, .result, .pager, .search-back-button, .search-close-button')) {
                this.closeSearchBar()
            }
        })

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.el.classList.contains(GLOBAL_CONSTANTS.CLASSES.SEARCH_ACTIVE)) {
                this.closeSearchBar()
            }
        })
    }

    handleResize() {
        if (
            this.el.classList.contains(
                GLOBAL_CONSTANTS.CLASSES.MOBILE_NAV_ACTIVE,
            ) &&
            !isTablet()
        ) {
            this.closeNavMenu()
            this.closeSearchBar()
        }
    }

    /**
     * @desc Handle key down for menu to set proper focus
     */
    handleMenuKeyDown(e) {
        const keyCode = e.keyCode || e.which

        if (
            this.el.classList.contains(
                GLOBAL_CONSTANTS.CLASSES.MOBILE_NAV_ACTIVE,
            ) &&
            keyCode === 27
        ) {
            // Handles escape key
            this.closeNavMenu()
            this.navMenuToggle.focus()
        } else if (
            e.target === this.tabItems[this.tabItems.length - 1] &&
            !e.shiftKey &&
            keyCode === 9
        ) {
            // Handles tabbing past the last menu item to return to menu toggle button

            event.preventDefault()
            this.tabItems[0].focus()
        } else if (
            e.target === this.tabItems[0] &&
            e.shiftKey &&
            keyCode === 9
        ) {
            // Handles shift-tabbing past the first menu item
            event.preventDefault()
            this.tabItems[this.tabItems.length - 1].focus()
        } else if (keyCode === 32) {
            // Handles spacebar for click
            e.target.click()
        }
    }

    openSearchBar() {
        this.el.classList.add(GLOBAL_CONSTANTS.CLASSES.SEARCH_ACTIVE)
        this.searchScrim.classList.add(GLOBAL_CONSTANTS.CLASSES.ACTIVE)
        this.searchWrapper.classList.add(GLOBAL_CONSTANTS.CLASSES.ACTIVE)
        resetCustomColors('custom_navbar_logo_color', 'custom_navbar_text_color', 'custom_navbar_color')
        this.loadRealTimeSearch()
        this.focusSearchInput()
    }

    focusSearchInput() {
        const input = this.searchForm.querySelector(CLASSES.SEARCH_INPUT)
        if (input) {
            input.focus()
        }
    }

    loadRealTimeSearch() {
        if (this.realTimeSearchLoad) {
            return
        }
        // Prefetched at browser idle so the first open does not wait on it, and
        // kept out of the entry bundle: nothing before the search bar opens
        // needs it.
        this.realTimeSearchLoad = import(/* webpackPrefetch: true */ './RealTimeSearch')
            .then((module) => {
                const RealTimeSearch = module.default
                new RealTimeSearch(document.querySelector(CLASSES.REAL_TIME_SEARCH))
                // Keystrokes that landed before RealTimeSearch bound its keyup
                // handler are lost, so replay whatever is in the input.
                const input = this.searchForm.querySelector(CLASSES.SEARCH_INPUT)
                if (input && input.value) {
                    input.dispatchEvent(new KeyboardEvent('keyup', {bubbles: true}))
                }
            })
            .catch((error) => {
                // Cleared so a failed load retries on the next open.
                this.realTimeSearchLoad = null
                Sentry.captureException(error)
            })
    }

    closeSearchBar() {
        if (
            this.el.classList.contains(GLOBAL_CONSTANTS.CLASSES.SEARCH_ACTIVE)
        ) {
            setCustomColors('custom_navbar_logo_color', 'custom_navbar_text_color', 'custom_navbar_color')
            this.searchScrim.classList.remove(GLOBAL_CONSTANTS.CLASSES.ACTIVE)
            this.searchWrapper.classList.remove(GLOBAL_CONSTANTS.CLASSES.ACTIVE)

            this.el.classList.remove(GLOBAL_CONSTANTS.CLASSES.SEARCH_ACTIVE)
            Emitter.emit('close-search-content')
        }
    }

    toggleSearchBar(event) {
        event.preventDefault()

        if (
            !this.el.classList.contains(
                GLOBAL_CONSTANTS.CLASSES.SEARCH_ACTIVE,
            )
        ) {
            requestAnimationFrame(this.openSearchBar)
        } else {
            requestAnimationFrame(this.closeSearchBar)
        }
    }

    /**
     * @desc Handles orientation change by firing our handleScroll function
     */
    handleOrientationChange() {
        this.handleScroll(true)
    }

    /**
     * @desc Scroll event that determines if the nav should show or hide.
     */
    handleScroll(hasOrientationChanged) {
        if (hasOrientationChanged) {
            this.setActive(false)
            return
        }

        if (this.position === window.pageYOffset) {
            return
        }

        const pos = window.pageYOffset
        const isScrollingUp = pos < this.position

        if (
            this.el.classList.contains(GLOBAL_CONSTANTS.CLASSES.SEARCH_ACTIVE)
        ) {
            // todo: this.closeSearchBar()
        }
        if (isScrollingUp) {
            this.scrollUpCount += this.position - pos
        } else {
            this.scrollUpCount = 0
        }

        const isScrollUpThresholdMet = this.scrollUpCount >= SCROLL_UP_BUFFER

        const NAV_HEIGHT = this.el.getBoundingClientRect().height

        if (pos > NAV_HEIGHT) {
            this.setActive(isScrollUpThresholdMet)
            this.setFixed(true)
        } else if ((isScrollingUp && pos === 0) || pos <= 1) {
            this.setActive(false)
            this.setFixed(false)
        }

        this.position = window.pageYOffset
    }

    toggleNavMenu(e) {
        e.preventDefault()
        if (
            this.el.classList.contains(
                GLOBAL_CONSTANTS.CLASSES.MOBILE_NAV_ACTIVE,
            )
        ) {
            this.closeNavMenu()
        } else {
            this.openNavMenu()
            this.closeSearchBar()
        }
    }

    closeNavMenu() {
        Emitter.emit('close-search-content')
        Emitter.off('keydown', this.handleMenuKeyDown)
        this.el.classList.remove(GLOBAL_CONSTANTS.CLASSES.MOBILE_NAV_ACTIVE)
        setCustomColors()
        this.navMenuToggle.setAttribute('aria-expanded', false)
        this.navMenuToggle.setAttribute('aria-label', 'Open Menu')
        this.navMenu.setAttribute('aria-hidden', true)

        enableBodyScroll(this.navMenu)
        this.navTimeout = setTimeout(() => {
            this.navMenu.classList.remove(GLOBAL_CONSTANTS.CLASSES.VISIBLE)
        }, GLOBAL_CONSTANTS.TIMING.STD_ANIM_TIME)
    }

    openNavMenu() {
        Emitter.on('keydown', this.handleMenuKeyDown)
        clearTimeout(this.navTimeout)

        this.el.classList.add(GLOBAL_CONSTANTS.CLASSES.MOBILE_NAV_ACTIVE)
        resetCustomColors()
        this.navMenuToggle.setAttribute('aria-expanded', true)
        this.navMenuToggle.setAttribute('aria-label', 'Close Menu')
        this.navMenu.setAttribute('aria-hidden', false)
        this.navMenu.classList.add(GLOBAL_CONSTANTS.CLASSES.VISIBLE)

        disableBodyScroll(this.navMenu)
    }

    setActive(isActive) {
        this.isActive = isActive
        if (isActive) {
            this.el.classList.add(GLOBAL_CONSTANTS.CLASSES.ACTIVE)
            document.body.classList.add(GLOBAL_CONSTANTS.CLASSES.HEADER_COMPACT)

        } else {
            this.el.classList.remove(GLOBAL_CONSTANTS.CLASSES.ACTIVE)
            document.body.classList.remove(GLOBAL_CONSTANTS.CLASSES.HEADER_COMPACT)
        }
    }

    setFixed(isFixed) {
        this.isFixed = isFixed
        if (isFixed) {
            this.el.classList.add(GLOBAL_CONSTANTS.CLASSES.FIXED)
            document.body.classList.remove(GLOBAL_CONSTANTS.CLASSES.HEADER_FULL)
        } else {
            this.el.classList.remove(GLOBAL_CONSTANTS.CLASSES.FIXED)
            document.body.classList.add(GLOBAL_CONSTANTS.CLASSES.HEADER_FULL)
        }
    }

    /**
     * @desc Tear down the event listeners
     */
    tearDown() {
        Emitter.off(GLOBAL_CONSTANTS.EVENTS.SCROLL, this.handleScroll)

        clearAllBodyScrollLocks()
    }
}

/**
 * @desc Test component definition used in module-loader
 */

export const NavComponent = {
    name: 'Nav',
    componentClass: CLASSES.COMPONENT,
    Source: Nav,
}
